import React, { useCallback, useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { HTTPResponse } from '@/class/HTTPResponse';
import { useAuthContext } from '../../context/AuthContext';
import { fetchData } from '../../lib/api';
import { adminCardAccent, adminMutedText, adminSectionTitle } from '../layout/adminStyles';

interface TweetChartItem {
  date: string;
  count: number;
}

interface DailyTweetCount {
  tweet_date: string;
  tweet_count: number;
}

const formatChartDate = (tweetDate: string): string => {
  const date = new Date(`${tweetDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return tweetDate;
  }

  return date.toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'Asia/Tokyo',
  });
};

const TweetChart: React.FC = () => {
  const { store } = useAuthContext();
  const [chartData, setChartData] = useState<TweetChartItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMonthlyTweetCounts = useCallback(async () => {
    if (!store?.id) {
      setChartData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const res: HTTPResponse = await fetchData(`store/${store.id}/tweet/monthly`);
      const counts = Array.isArray(res.data?.counts) ? (res.data.counts as DailyTweetCount[]) : [];

      setChartData(
        counts.map((item) => ({
          date: formatChartDate(item.tweet_date),
          count: item.tweet_count,
        }))
      );
    } catch (err: unknown) {
      console.error(err);
      setChartData([]);
      setErrorMessage('つぶやき数の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  }, [store?.id]);

  useEffect(() => {
    loadMonthlyTweetCounts();
  }, [loadMonthlyTweetCounts]);

  return (
    <div className={`${adminCardAccent} border-l-amber-400 bg-gradient-to-br from-white to-amber-50/20`}>
      <h4 className={`${adminSectionTitle} mb-4 border-l-amber-400 pb-0`}>つぶやき数の推移（過去31日）</h4>

      {isLoading ? (
        <p className={`${adminMutedText} text-sm`}>読み込み中...</p>
      ) : errorMessage ? (
        <p className={`${adminMutedText} text-sm`}>{errorMessage}</p>
      ) : chartData.length === 0 ? (
        <p className={`${adminMutedText} text-sm`}>表示できるつぶやきデータがありません。</p>
      ) : (
        <div className="h-[220px] sm:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="date" tick={{ fill: '#78716c', fontSize: 12 }} />
              <YAxis
                allowDecimals={false}
                tick={{ fill: '#78716c', fontSize: 12 }}
                label={{ value: '件数', angle: -90, position: 'insideLeft', fill: '#57534e', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: '12px',
                  border: '1px solid #e7e5e4',
                  backgroundColor: '#fff',
                }}
              />
              <Line
                type="monotone"
                dataKey="count"
                name="つぶやき数"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={{ fill: '#fbbf24', strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: '#d97706' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default TweetChart;
